import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  MenuItem,
  Box,
  Autocomplete,
  ListSubheader
} from "@mui/material";

import { AuthContext } from "../../context/AuthProvider";
import { fetchWithInterceptor } from "../../utils/fetchWithInterceptor";


const INITIAL_VALUES = {
  name: "",
  type: "",
  parentCountryId: "",
  parentRegionId: "",
};

export default function LocationFormDialog({ open, onClose, onSave, rowId, location }) {
  const [data, setData] = useState(INITIAL_VALUES);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const authContext = useContext(AuthContext);

  useEffect(() => {
    if (!open) return;

    if (rowId && location) {
      setData({
        name: location.name || "",
        type: location.type || "",
        parentCountryId: location.parentCountryId ?? "",
        parentRegionId: location.parentRegionId ?? "",
      });
    } else {
      setData(INITIAL_VALUES);
    }
  }, [rowId, location, open]);

  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [countriesRes, regionsRes] = await Promise.all([
          fetchWithInterceptor("/api/locations?type=country&isActive=true&limit=500", {
            headers: {
              "Content-Type": "application/json",
              "x-access-token": authContext.getToken(),
            },
          }),
          fetchWithInterceptor("/api/locations?type=region&isActive=true&limit=500", {
            headers: {
              "Content-Type": "application/json",
              "x-access-token": authContext.getToken(),
            },
          }),
        ]);

        if (countriesRes.ok) {
          const countriesData = await countriesRes.json();
          setCountries(countriesData.rows || []);
        }
        if (regionsRes.ok) {
          const regionsData = await regionsRes.json();
          setRegions(regionsData.rows || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open, authContext]);


  const regionsSortedByCountry = useMemo(() => {
    return [...regions].sort((a, b) => {
      const nameA = a.parentCountry?.name ?? "";
      const nameB = b.parentCountry?.name ?? "";
      return nameA.localeCompare(nameB);
    });
  }, [regions]);

  const selectedCountry = useMemo(() => {
    if (!data.parentCountryId) return null;
    return countries.find((c) => c.id === Number(data.parentCountryId)) ?? null;
  }, [countries, data.parentCountryId]);

  const selectedRegion = useMemo(() => {
    if (!data.parentRegionId) return null;
    return regions.find((r) => r.id === Number(data.parentRegionId)) ?? null;
  }, [regions, data.parentRegionId]);

  const handleClose = () => {
    setData(INITIAL_VALUES);
    onClose();
  };

  const handleTypeChange = (value) => {
    setData((prev) => ({
      ...prev,
      type: value,
      parentCountryId: "",
      parentRegionId: "",
    }));
  };

  const isValid = () => {
    if (!data.name.trim() || !data.type) return false;

    if (data.type === "region") {
      if (!selectedCountry) {
        return false;
      }
    }

    if (data.type === "subregion") {
      if (!selectedRegion) {
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    const payload = {
      name: data.name.trim(),
      type: data.type,
    };

    if (data.type === "region") {
      payload.parentCountryId = Number(data.parentCountryId);
    }
    if (data.type === "subregion") {
      payload.parentRegionId = Number(data.parentRegionId);
    }

    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{rowId ? "Edit Location" : "Create Location"}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          {rowId
            ? "Update the location information."
            : "Fill out the information to create a new location."}
        </DialogContentText>

        <Box
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <TextField
            label="Name"
            fullWidth
            required
            autoComplete="off"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            helperText="This field is required"
          />

          <TextField
            select
            label="Type"
            fullWidth
            required
            autoComplete="off"
            value={data.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            helperText="This field is required"
          >
            <MenuItem value="">Select type</MenuItem>
            <MenuItem value="country">Country</MenuItem>
            <MenuItem value="region">Region</MenuItem>
            <MenuItem value="subregion">Subregion</MenuItem>
          </TextField>

          {data.type === "region" && (
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.name}
              value={selectedCountry}
              onChange={(_, newValue) =>
                setData({ ...data, parentCountryId: newValue ? newValue.id : "" })
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disabled={loadingOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Country"
                  required
                  autoComplete="off"
                  helperText="Region must belong to a country"
                />
              )}
            />
          )}

          {data.type === "subregion" && (
            <Autocomplete
              options={regionsSortedByCountry}
              groupBy={(option) => option.parentCountry?.name ?? ""}
              getOptionLabel={(option) => option.name}
              value={selectedRegion}
              onChange={(_, newValue) =>
                setData({ ...data, parentRegionId: newValue ? newValue.id : "" })
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disabled={loadingOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Region"
                  required
                  autoComplete="off"
                  helperText="Subregion must belong to a region"
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <ListSubheader
                    component="div"
                    sx={{
                      bgcolor: "background.default",
                      fontWeight: 800,
                    }}
                  >
                    {params.group}
                  </ListSubheader>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              )}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
